<template>
  <div class="access-tokens-panel">
    <AlertBanner
      v-if="alert"
      :type="alert.type"
      :title="alert.title"
      :message="alert.message"
      @dismiss="alert = null"
    />

    <div v-if="createdToken" class="created-token-alert">
      <div>
        <strong>{{ s__('AccessTokens|Your new access token') }}</strong>
        <p>
          {{
            s__(
              'AccessTokens|Copy it now. You will not be able to see this token again after leaving this page.'
            )
          }}
        </p>
      </div>
      <div class="created-token-row">
        <code>{{ displayedCreatedToken }}</code>
        <div class="created-token-actions">
          <button
            type="button"
            class="btn-secondary created-token-action-button"
            :title="createdTokenVisible ? s__('AccessTokens|Hide') : s__('AccessTokens|Show')"
            :aria-label="
              createdTokenVisible ? s__('AccessTokens|Hide token') : s__('AccessTokens|Show token')
            "
            @click="createdTokenVisible = !createdTokenVisible"
          >
            <EyeIcon size="16" color="currentColor" />
            <span class="created-token-action-label">
              {{ createdTokenVisible ? s__('AccessTokens|Hide') : s__('AccessTokens|Show') }}
            </span>
          </button>
          <button
            type="button"
            class="btn-secondary created-token-action-button"
            :title="s__('AccessTokens|Copy')"
            :aria-label="s__('AccessTokens|Copy token')"
            @click="copyCreatedToken"
          >
            <CopyIcon size="16" color="currentColor" />
            <span class="created-token-action-label">{{ s__('AccessTokens|Copy') }}</span>
          </button>
        </div>
      </div>
      <p
        v-if="createdTokenCopyMessage"
        class="created-token-copy-message"
        role="status"
        aria-live="polite"
      >
        {{ createdTokenCopyMessage }}
      </p>
    </div>

    <div class="api-docs-hint">
      <div>
        <strong>{{ s__('AccessTokens|REST API documentation') }}</strong>
        <p>
          {{
            s__(
              'AccessTokens|Use personal access tokens with the public REST API. The interactive Swagger page documents available endpoints, scopes, request parameters, and response examples.'
            )
          }}
        </p>
      </div>
      <a href="/api-docs" target="_blank" rel="noopener noreferrer" class="btn-secondary">
        {{ s__('AccessTokens|Open API docs') }}
      </a>
    </div>

    <form class="access-token-form" @submit.prevent="createToken">
      <div class="form-row">
        <label for="access-token-name">{{ s__('AccessTokens|Token name') }}</label>
        <input
          id="access-token-name"
          v-model.trim="form.name"
          type="text"
          :placeholder="s__('AccessTokens|Example: automation script')"
          :disabled="creating"
          required
        />
      </div>

      <div class="form-row">
        <label for="access-token-project">{{ s__('AccessTokens|Project restriction') }}</label>
        <select id="access-token-project" v-model="form.projectId" :disabled="creating || loading">
          <option value="">{{ s__('AccessTokens|All accessible projects') }}</option>
          <option v-for="project in projects" :key="project.id" :value="project.id">
            {{ project.title }}
          </option>
        </select>
        <div class="help-text">
          {{
            s__(
              'AccessTokens|When a project is selected, this token can only be used for resources in that project.'
            )
          }}
        </div>
      </div>

      <div class="form-row">
        <label>{{ s__('AccessTokens|Expiration date') }} {{ __('(optional)') }}</label>
        <DateTimeInput
          class="access-token-expiration-input"
          mode="date"
          :date="form.expiresAt"
          :disabled="creating"
          :title="s__('AccessTokens|Expiration date')"
          @update:date="form.expiresAt = $event || ''"
        />
        <div class="help-text">
          {{ s__('AccessTokens|Leave empty if this token should not expire automatically.') }}
        </div>
      </div>

      <div class="form-row">
        <label>{{ s__('AccessTokens|Scopes') }}</label>
        <div class="scope-list">
          <label v-for="scope in readScopes" :key="scope.value" class="scope-option is-readonly">
            <input type="checkbox" checked disabled />
            <span>{{ scope.label }}</span>
          </label>
          <label v-for="scope in modifyScopes" :key="scope.value" class="scope-option">
            <input
              v-model="form.modifyScopes"
              type="checkbox"
              :value="scope.value"
              :disabled="creating"
            />
            <span>{{ scope.label }}</span>
          </label>
        </div>
      </div>

      <div class="form-actions">
        <button type="submit" class="btn-primary" :disabled="creating || !form.name">
          {{ creating ? s__('AccessTokens|Creating...') : s__('AccessTokens|Create access token') }}
        </button>
      </div>
    </form>

    <div v-if="loading" class="muted">{{ __('Loading…') }}</div>
    <div v-else-if="tokens.length === 0" class="muted">
      {{ s__('AccessTokens|No personal access tokens yet.') }}
    </div>
    <div v-else class="access-token-list">
      <div v-for="token in sortedTokens" :key="token.id" class="access-token-item">
        <div class="access-token-main">
          <div class="access-token-title-row">
            <strong>{{ token.name }}</strong>
            <span :class="['access-token-status', { revoked: !token.active }]">
              {{ token.active ? s__('AccessTokens|Active') : s__('AccessTokens|Inactive') }}
            </span>
          </div>
          <div class="access-token-meta">
            <span>{{ token.project?.title || s__('AccessTokens|All projects') }}</span>
            <span>{{ s__('AccessTokens|Created') }} {{ formatDate(token.createdAt) }}</span>
            <span v-if="token.expiresAt"
              >{{ s__('AccessTokens|Expires') }} {{ formatDate(token.expiresAt) }}</span
            >
            <span v-if="token.lastUsedAt"
              >{{ s__('AccessTokens|Last used') }} {{ formatDate(token.lastUsedAt) }}</span
            >
          </div>
          <div class="scope-tags">
            <span v-for="scope in token.scopes" :key="scope" class="scope-tag">
              {{ scopeLabel(scope) }}
            </span>
          </div>
        </div>
        <button
          type="button"
          class="btn-secondary"
          :disabled="!token.active || revokingId === token.id"
          @click="revokeToken(token)"
        >
          {{
            revokingId === token.id ? s__('AccessTokens|Revoking...') : s__('AccessTokens|Revoke')
          }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { __, s__ } from '@/locale'
import AlertBanner from '../common/AlertBanner.vue'
import DateTimeInput from '../common/DateTimeInput.vue'
import CopyIcon from '../common/icons/CopyIcon.vue'
import EyeIcon from '../common/icons/EyeIcon.vue'

const READ_SCOPE_VALUES = ['read_tasks', 'read_labels', 'read_calendar_events']
const MODIFY_SCOPE_VALUES = ['modify_tasks', 'modify_labels', 'modify_calendar_events']

const readScopes = [
  { value: 'read_tasks', label: s__('AccessTokens|Read tasks') },
  { value: 'read_labels', label: s__('AccessTokens|Read labels') },
  { value: 'read_calendar_events', label: s__('AccessTokens|Read calendar events') },
]

const modifyScopes = [
  { value: 'modify_tasks', label: s__('AccessTokens|Modify tasks') },
  { value: 'modify_labels', label: s__('AccessTokens|Modify labels') },
  { value: 'modify_calendar_events', label: s__('AccessTokens|Modify calendar events') },
]

const ACCESS_TOKENS_QUERY = gql`
  query ProfilePersonalAccessTokens {
    currentUser {
      id
      personalAccessTokens {
        id
        name
        scopes
        active
        createdAt
        expiresAt
        lastUsedAt
        revokedAt
        project {
          id
          title
        }
      }
      projects(first: 100) {
        edges {
          node {
            id
            title
          }
        }
      }
    }
  }
`

const CREATE_ACCESS_TOKEN_MUTATION = gql`
  mutation CreatePersonalAccessToken(
    $name: String!
    $projectId: ID
    $scopes: [String!]
    $expiresAt: ISO8601DateTime
  ) {
    createPersonalAccessToken(
      name: $name
      projectId: $projectId
      scopes: $scopes
      expiresAt: $expiresAt
    ) {
      token
      personalAccessToken {
        id
        name
        scopes
        active
        createdAt
        expiresAt
        lastUsedAt
        revokedAt
        project {
          id
          title
        }
      }
      errors
    }
  }
`

const REVOKE_ACCESS_TOKEN_MUTATION = gql`
  mutation RevokePersonalAccessToken($id: ID!) {
    revokePersonalAccessToken(id: $id) {
      personalAccessToken {
        id
        active
        revokedAt
      }
      errors
    }
  }
`

const form = reactive({
  name: '',
  projectId: '',
  expiresAt: '',
  modifyScopes: [],
})
const alert = ref(null)
const createdToken = ref('')
const createdTokenVisible = ref(false)
const createdTokenCopyMessage = ref('')
const creating = ref(false)
const revokingId = ref(null)

const { result, loading, refetch } = useQuery(ACCESS_TOKENS_QUERY)
const { mutate: createAccessTokenMutation } = useMutation(CREATE_ACCESS_TOKEN_MUTATION)
const { mutate: revokeAccessTokenMutation } = useMutation(REVOKE_ACCESS_TOKEN_MUTATION)

const tokens = computed(() => result.value?.currentUser?.personalAccessTokens || [])
const sortedTokens = computed(() => {
  return [...tokens.value].sort((left, right) => Number(right.active) - Number(left.active))
})
const projects = computed(() => {
  return (result.value?.currentUser?.projects?.edges || []).map(edge => edge.node)
})
const displayedCreatedToken = computed(() => {
  return createdTokenVisible.value
    ? createdToken.value
    : 'trpat_*******************************************'
})

function scopeLabel(scope) {
  return [...readScopes, ...modifyScopes].find(option => option.value === scope)?.label || scope
}

function formatDate(value) {
  if (!value) return ''
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value))
}

function expirationIso8601() {
  if (!form.expiresAt) return null
  return `${form.expiresAt}T23:59:59`
}

async function createToken() {
  try {
    creating.value = true
    alert.value = null
    const scopes = [
      ...READ_SCOPE_VALUES,
      ...form.modifyScopes.filter(scope => MODIFY_SCOPE_VALUES.includes(scope)),
    ]

    const { data } = await createAccessTokenMutation({
      name: form.name,
      projectId: form.projectId || null,
      scopes,
      expiresAt: expirationIso8601(),
    })

    if (data?.createPersonalAccessToken?.errors?.length) {
      throw new Error(data.createPersonalAccessToken.errors.join(', '))
    }

    createdToken.value = data.createPersonalAccessToken.token
    createdTokenVisible.value = false
    createdTokenCopyMessage.value = ''
    form.name = ''
    form.projectId = ''
    form.expiresAt = ''
    form.modifyScopes = []
    await refetch()
  } catch (e) {
    alert.value = {
      type: 'error',
      title: s__('AccessTokens|Failed to create access token'),
      message: e.message || s__('AccessTokens|Unknown error'),
    }
  } finally {
    creating.value = false
  }
}

async function revokeToken(token) {
  if (!window.confirm(s__('AccessTokens|Revoke this access token?'))) return

  try {
    revokingId.value = token.id
    alert.value = null
    const { data } = await revokeAccessTokenMutation({ id: token.id })

    if (data?.revokePersonalAccessToken?.errors?.length) {
      throw new Error(data.revokePersonalAccessToken.errors.join(', '))
    }

    await refetch()
  } catch (e) {
    alert.value = {
      type: 'error',
      title: s__('AccessTokens|Failed to revoke access token'),
      message: e.message || s__('AccessTokens|Unknown error'),
    }
  } finally {
    revokingId.value = null
  }
}

async function copyCreatedToken() {
  if (!createdToken.value) return

  try {
    await navigator.clipboard.writeText(createdToken.value)
    createdTokenCopyMessage.value = s__('AccessTokens|Token copied')
  } catch {
    createdTokenCopyMessage.value = s__('AccessTokens|Failed to copy token')
  }
}
</script>

<style scoped>
.access-tokens-panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.created-token-alert {
  padding: 12px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  color: #333;
}

.created-token-alert p {
  margin: 4px 0 0;
  color: #666;
}

.created-token-row {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.created-token-actions {
  display: flex;
  gap: 8px;
}

.created-token-action-button {
  display: inline-flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
}

.created-token-row code {
  flex: 1;
  min-width: 0;
  padding: 8px;
  overflow-x: auto;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
}

.created-token-copy-message {
  display: inline-flex;
  margin: 10px 0 0;
  padding: 6px 10px;
  color: #065f46;
  font-size: 13px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
}

.api-docs-hint {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  background: #f5f5f5;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.api-docs-hint p {
  margin: 4px 0 0;
  color: #666;
}

.api-docs-hint .btn-secondary {
  flex: 0 0 auto;
  text-decoration: none;
}

.access-token-form,
.access-token-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.scope-list {
  display: grid;
  gap: 8px;
}

.scope-option {
  display: flex;
  gap: 8px;
  align-items: center;
  color: #333;
}

.scope-option.is-readonly {
  color: #666;
}

.form-actions {
  display: flex;
  justify-content: flex-start;
}

.access-token-item {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.access-token-main {
  min-width: 0;
}

.access-token-title-row,
.access-token-meta,
.scope-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.access-token-meta {
  margin-top: 4px;
  color: #666;
  font-size: 12px;
}

.access-token-status,
.scope-tag {
  padding: 2px 6px;
  font-size: 12px;
  background: #e8f5e9;
  color: #166534;
  border-radius: 999px;
}

.access-token-status.revoked {
  background: #f5f5f5;
  color: #666;
}

.scope-tags {
  margin-top: 8px;
}

.scope-tag {
  background: #f5f5f5;
  color: #333;
}

@media (max-width: 768px) {
  .api-docs-hint {
    flex-direction: column;
  }

  .created-token-row {
    flex-direction: row;
    align-items: stretch;
  }

  .created-token-actions {
    flex: 0 0 auto;
  }

  .created-token-action-button {
    width: 40px;
    padding-right: 0;
    padding-left: 0;
  }

  .created-token-action-label {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .access-token-item {
    flex-direction: column;
  }
}
</style>
